import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTags } from '../../../../settings/tags/hooks/useTags';

interface UseTagSelectorProps {
    selectedTags: string[];
    onTagsChange: (tagIds: string[]) => void;
}

export const useTagSelector = ({ selectedTags, onTagsChange }: UseTagSelectorProps) => {
    const { tags, createTag, updateTag, deleteTag } = useTags();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Fecha dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Memoiza tags selecionadas
    const selectedTagObjects = useMemo(
        () => tags.filter(tag => selectedTags.includes(tag.id)),
        [tags, selectedTags]
    );

    // Memoiza tags filtradas pela busca
    const filteredTags = useMemo(
        () => tags.filter(tag =>
            tag.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
            !selectedTags.includes(tag.id)
        ),
        [tags, searchTerm, selectedTags]
    );

    // Handler para adicionar tag
    const handleAddTag = useCallback((tagId: string) => {
        if (!tagId) {
            console.warn('[useTagSelector] ID de tag inválido');
            return;
        }
        onTagsChange([...selectedTags, tagId]);
        setSearchTerm('');
    }, [selectedTags, onTagsChange]);

    // Handler para remover tag
    const handleRemoveTag = useCallback((tagId: string) => {
        if (!tagId) {
            console.warn('[useTagSelector] ID de tag inválido');
            return;
        }
        onTagsChange(selectedTags.filter(id => id !== tagId));
    }, [selectedTags, onTagsChange]);

    // Handlers para edição e exclusão (CRUD completo)
    const [tagToEdit, setTagToEdit] = useState<{ id: string, name: string, color: string } | undefined>(undefined);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [tagToDelete, setTagToDelete] = useState<{ id: string, name: string } | undefined>(undefined);

    // Handler para criar nova tag
    const handleCreateTag = useCallback(async (tagData: { name: string; color: string }) => {
        if (!tagData.name || !tagData.color) {
            console.error('[useTagSelector] Dados de tag inválidos');
            return;
        }

        try {
            await createTag(tagData);
            setIsCreateModalOpen(false);
            setSearchTerm('');
        } catch (error) {
            console.error('[useTagSelector] Erro ao criar tag:', error);
        }
    }, [createTag]);

    // Handler para atualizar tag
    const handleUpdateTag = useCallback(async (tagData: { name: string; color: string }) => {
        if (!tagToEdit || !tagData.name || !tagData.color) return;

        try {
            await updateTag(tagToEdit.id, tagData);
            setIsCreateModalOpen(false);
            setTagToEdit(undefined);
        } catch (error) {
            console.error('[useTagSelector] Erro ao atualizar tag:', error);
        }
    }, [updateTag, tagToEdit]);

    // Handler para deletar tag (do sistema)
    const handleDeleteTagSystem = useCallback(async () => {
        if (!tagToDelete) return;
        try {
            await deleteTag(tagToDelete.id);
            setIsDeleteModalOpen(false);
            setTagToDelete(undefined);
        } catch (error) {
            console.error('[useTagSelector] Erro ao deletar tag:', error);
        }
    }, [deleteTag, tagToDelete]);

    const openEditModal = (tag: any) => {
        setTagToEdit(tag);
        setIsCreateModalOpen(true);
    };

    const openDeleteModal = (tag: any) => {
        setTagToDelete(tag);
        setIsDeleteModalOpen(true);
    };

    return {
        // Estado
        isOpen,
        setIsOpen,
        searchTerm,
        setSearchTerm,
        isCreateModalOpen,
        setIsCreateModalOpen,
        dropdownRef,
        tagToEdit,
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        tagToDelete,
        setTagToDelete,

        // Dados computados
        selectedTagObjects,
        filteredTags,

        // Handlers
        handleAddTag,
        handleRemoveTag,
        handleCreateTag,
        handleUpdateTag,
        handleDeleteTagSystem,
        openEditModal,
        openDeleteModal
    };
};
